import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRight, Banknote, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import "../assets/css/GroupLoanEmiDueReport.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import {useNavigate} from 'react-router-dom'

const GroupLoanEmiDueReport = () => {
  const dispatch = useDispatch();
  const navigate=useNavigate();
  const { profile } = useSelector((state) => state.userProfile);

  /* ---------------- STATES ---------------- */
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dayFilter, setDayFilter] = useState("");
  const [loanTypeFilter, setLoanTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [emiData, setEmiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [company, setCompany] = useState("");

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100, "All"];

  const companyName = company?.name || profile?.branchName || "Company";

  const selectedCenterName =
    centers.find((c) => c.id == selectedCenter)?.CenterName || "All";

  const selectedGroupName =
    groups.find((g) => g.id == selectedGroup)?.groupname || "All";

  const selectedFilterName = dayFilter || "All";


   // Custom styling for react-select to match your form UI
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    height: "40px",
    minHeight: "40px",
    borderRadius: "6px",
    borderColor: state.isDisabled
      ? "#e2e8f0"
      : state.isFocused
      ? "#2563eb"
      : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.15)" : "none",
    fontSize: "14px",
    backgroundColor: state.isDisabled ? "#f8fafc" : "#ffffff",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      borderColor: state.isDisabled
        ? "#e2e8f0"
        : state.isFocused
        ? "#2563eb"
        : "#94a3b8",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "40px",
    padding: "0 12px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
    color: "#0f172a",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#94a3b8" : "#0f172a",
    fontSize: "14px",
    fontWeight: "500",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#cbd5e1" : "#64748b",
    fontSize: "14px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "40px",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: "8px",
    color: state.isDisabled ? "#cbd5e1" : "#64748b",
    "&:hover": {
      color: state.isDisabled ? "#cbd5e1" : "#334155",
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "8px",
    color: "#94a3b8",
    "&:hover": {
      color: "#ef4444",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "6px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    marginTop: "4px",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    padding: "10px 12px",
    backgroundColor: state.isSelected
      ? "#2563eb"
      : state.isFocused
      ? "#eff6ff"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#1e293b",
    cursor: "pointer",
    active: {
      backgroundColor: "#dbeafe",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

  const formatDateDDMMYY = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear());
    return `${dd}/${mm}/${yy}`;
  };

  /* ---------------- PROFILE ---------------- */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ---------------- FETCH CENTERS ---------------- */
  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/group/centre?LoanofficerId=${profile.id}&limit=10000`,
        );
        setCenters(res.data?.data || []);
      } catch (error) {
        console.error("Center fetch error:", error);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  /* ---------------- FETCH GROUPS ---------------- */
 useEffect(() => {
    if (!selectedCenter?.value) {
      setGroups([]);
      setSelectedGroup(null);
      return;
    }

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter.value}&limit=10000`
        );
        setGroups(res.data?.data || []);
      } catch (error) {
        console.error("Group fetch error:", error);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  /* ---------------- FETCH EMI ---------------- */
  const fetchEmis = async (page = 1) => {
    if (!profile?.superAdminId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        "https://payyug.in/api/group-loan/emi/list-filter",
        {
          params: {
            superAdminId: profile.superAdminId,
            branchId: profile.branchId,
            centerId: selectedCenter?.value || undefined,
            groupId: selectedGroup?.value || undefined,
            page,
            filterType: dayFilter || undefined,
            loanType: loanTypeFilter || undefined,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            search: searchQuery || undefined,
            status:"PENDING",
            limit: itemsPerPage === "All" ? 100000 : itemsPerPage,
          },
        },
      );
      console.log("emi due", res.data);

      if (res.data?.success) {
        setEmiData(res.data.data || []);
        setCurrentPage(res.data.currentPage || page);
        setTotalPages(res.data.totalPages || 1);
        setTotalRecords(res.data.totalRecords || 0);
      } else {
        setEmiData([]);
        setTotalPages(1);
        setTotalRecords(0);
        toast.error(res.data.message || "No data found");
      }
    } catch (error) {
      console.error("EMI fetch error:", error);
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  };

  // Initial load auto trigger
  useEffect(() => {
    if (profile?.superAdminId) {
      fetchEmis(currentPage);
    }
  }, [currentPage, itemsPerPage, profile]);

  useEffect(() => {
    if (hasSearched) {
      const delayDebounceFn = setTimeout(() => {
        setCurrentPage(1);
        fetchEmis(1);
      }, 400);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchQuery]);

  const filteredData = emiData.filter((r) => {
    const text = `
      ${r.loanAccount?.loanAccountNumber}
      ${r.loanAccount?.memberName}
      ${r.loanAccount?.centerName}
      ${r.loanAccount?.groupName}
    `.toLowerCase();

    return text.includes(searchQuery.toLowerCase());
  });
  /* ---------------- FILTER SUBMIT ---------------- */
  const handleFilter = () => {
    setCurrentPage(1);
    fetchEmis(1);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`,
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };

    if (!profile?.superAdminId) return;
    fetchCompany();
  }, [profile?.superAdminId]);

  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((emi) => selectedIds.some((s) => s.id === emi.id));

  const handleSelectAll = (isChecked) => {
    const currentPageEmis = filteredData;

    setSelectedIds((prev) => {
      if (isChecked) {
        const newItems = currentPageEmis.filter(
          (emi) => !prev.some((p) => p.id === emi.id),
        );

        return [...prev, ...newItems];
      }

      return prev.filter(
        (p) => !currentPageEmis.some((emi) => emi.id === p.id),
      );
    });
  };

  const handleSelectClick = (emi, isChecked) => {
    setSelectedIds((prev) =>
      isChecked ? [...prev, emi] : prev.filter((item) => item.id !== emi.id),
    );
  };

  /* ================= BULK PAY ================= */
  const handlebulkpay = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one EMI");
      return;
    }

    const isConfirmed = window.confirm(
      "Are you sure you want to pay selected EMIs?",
    );
    if (!isConfirmed) return;

    setLoading(true);
    try {
      const paymentsPayload = selectedIds.map((emi) => ({
        emiId: Number(emi.id),
        amount: Number(emi.emiAmount),
      }));

      const requestBody = {
        paidDate: new Date().toISOString().split("T")[0],
        collectedBy: Number(localStorage.getItem("userId") || 12),
        paymentMode: "CASH",
        payments: paymentsPayload,
      };

      const res = await axios.post(
        "https://payyug.in/api/group-loan/emi/bulk-emi-pay",
        requestBody,
      );

      toast.success(res.data?.message || "Bulk Payment Successful");

      setSelectedIds([]);
      await fetchEmis(currentPage);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err ||
          "Bulk payment failed",
      );
    } finally {
      setLoading(false);
    }
  };


  const calculateOverdueAmount = (currentRow) => {
  if (!currentRow?.loanAccount?.loanAccountNumber) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return filteredData.reduce((sum, r) => {
    if (
      r.loanAccount?.loanAccountNumber !==
      currentRow.loanAccount?.loanAccountNumber
    ) {
      return sum;
    }

    if (!r.dueDate) return sum;

    const dueDate = new Date(r.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    const isUnpaid = !["paid", "success", "settled"].includes(
      r.status?.toLowerCase()
    );

    if (dueDate < today && isUnpaid) {
      return sum + Number(r.emiAmount || 0);
    }

    return sum;
  }, 0);
};

  // 🔹 Export to Excel
  const exportExcel = () => {
    const data = filteredData.map((item) => ({
      State: item.member?.state || "N/A",
      Region: profile?.branchName||item.member?.district || "N/A",
      Branch: profile?.branchName || "N/A",
      "Loan Officer Name": item.loanAccount?.officerName || "N/A",
      "Account No": item.loanAccount?.loanAccountNumber || "N/A",
      "Loan Type": `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
      "Loan Amount": item.loanAccount?.loanAmount
        ? item.loanAccount?.loanAmount
        : 0,
      "Center Name": item.loanAccount?.centerName || "N/A",
      "Group Name": item.loanAccount?.groupName || "N/A",
      "Member Name": item.loanAccount?.memberName || "N/A",
      "Guardian/Father/husband/Care of Name": item.member?.guardianName || "N/A",
      "Mobile No.": item.member?.mobileNumber || "N/A",
      "Emi No.": item?.installmentNo || "N/A",
      "Emi Amount": item.emiAmount ? item.emiAmount : 0,
      "Total Due Amount": calculateOverdueAmount(item),
      "Outstanding amount": item.outstanding ? item.outstanding : 0,
      "Due Date": item.dueDate ? formatDateDDMMYY(item.dueDate) : "N/A",
      "Disbursal Date": item?.loanAccount?.disbursalDate
        ? formatDateDDMMYY(item?.loanAccount?.disbursalDate)
        : "N/A",
      Status: item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    // 🔹 Header styling
    const headerCells = Object.keys(data[0]);
    headerCells.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cellRef].s = {
        font: {
          bold: true,
          color: { rgb: "FFFFFF" },
        },
        fill: {
          fgColor: { rgb: "1F4E78" }, // dark blue
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    });

    // 🔹 Auto column width
    worksheet["!cols"] = headerCells.map((key) => ({
      wch: Math.max(key.length + 5, 18),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Report");

    XLSX.writeFile(workbook, "Group_EmiDue_Report.xlsx");
  };

  // 🔹 Export to CSV
  const exportCSV = () => {
    const data = filteredData.map((item) => ({
      State: item.member?.state || "N/A",
      Region: profile?.branchName||item.member?.district || "N/A",
      Branch: profile?.branchName || "N/A",
      "Loan Officer Name": item.loanAccount?.officerName || "N/A",
      "Account No": item.loanAccount?.loanAccountNumber || "N/A",
      "Loan Type": `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
      "Loan Amount": item.loanAccount?.loanAmount
        ? item.loanAccount?.loanAmount
        : 0,
      "Center Name": item.loanAccount?.centerName || "N/A",
      "Group Name": item.loanAccount?.groupName || "N/A",
      "Member Name": item.loanAccount?.memberName || "N/A",
      "Guardian/Father/husband/Care of Name": item.member?.guardianName || "N/A",
      "Mobile No.": item.member?.mobileNumber || "N/A",
      "Emi No.": item.installmentNo || "N/A",
      "Emi Amount": item.emiAmount ? item.emiAmount : 0,
      "Outstanding amount": item.outstanding ? item.outstanding : 0,
      "Due Date": item.dueDate ? formatDateDDMMYY(item.dueDate) : "N/A",
      "Disbursal Date": item?.loanAccount?.disbursalDate
        ? formatDateDDMMYY(item?.loanAccount?.disbursalDate)
        : "N/A",
      Status: item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

    const blob = new Blob([csvOutput], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, "GroupLoan_EmiDue_Report.csv");
  };

  // 🔹 Export to PDF
  const exportPDF = async () => {
  const doc = new jsPDF("p", "mm", "a4",true);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // ================= 2. HEADER SETTINGS =================
  const margin = 10;
  const headerY = 15;
  const logoSize = 22;
  const centerX = pageWidth / 2;

    // Draw Page Border (Matches LoanCard style)
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

  // --- LOGO (Left) ---
  if (company?.logoUrl) {
    try {
      const response = await fetch(company.logoUrl);
      const blob = await response.blob();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      // Square logo box like the card
      doc.rect(margin, headerY, logoSize + 4, logoSize + 4); 
      doc.addImage(base64, "PNG", margin + 2, headerY + 2, logoSize, logoSize);
    } catch (e) {
      console.error("Logo fetch failed", e);
    }
  }

  // --- COMPANY DETAILS (Center) ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const companyName = (company?.name || "COMPANY NAME").toUpperCase();
  doc.text(companyName, centerX, headerY + 5, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  let detailsY = headerY + 11;
  const lines = [
    `CIN: ${company?.cin || "-"}`,
    `Regd. Office: ${company?.address || "-"}`,
    // `Website: ${company?.domain || "-"}`,
    `Contact: ${company?.contact || "-"}`,
    `Email: ${company?.email || "-"}`
  ];    // Divider
   lines.forEach((line) => {
    const splitText = doc.splitTextToSize(line, pageWidth - 110);
    doc.text(splitText, centerX, detailsY, { align: "center" });
    detailsY += (splitText.length * 4.5);
  });

   // ================= 3. DIVIDER & TITLE =================
  let currentY = Math.max(detailsY, headerY + logoSize) + 5;
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);

  currentY += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Group Loan Emi Due Report", centerX, currentY, { align: "center" });
  
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${new Date().toLocaleDateString("en-GB")}`, pageWidth - margin - 2, currentY, { align: "right" });

    const tableColumn = [
      "SN",
      "Account No",
      "Loan Type",
      "Loan Amount",
      "Center",
      "Group",
      "Member",
      "Mobile",
      "Emi No.",
      "Emi Amount",
      "Due Date",
      "Status",
    ];
    const tableRows = [];

    filteredData.forEach((item, index) => {
      const loanData = [
        index + 1,
        item.loanAccount?.loanAccountNumber || "N/A",
        `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
        item.loanAccount?.loanAmount ? item.loanAccount?.loanAmount : 0,
        item.loanAccount?.centerName || "N/A",
        item.loanAccount?.groupName || "N/A",
        item.loanAccount?.memberName || "N/A",
        item.member?.mobileNumber || "N/A",
        item.installmentNo || "N/A",
        item.emiAmount ? item.emiAmount : 0,
        item.dueDate ? formatDateDDMMYY(item.dueDate) : "N/A",
        item.status || "N/A",
      ];
      tableRows.push(loanData);
    });

          const totalRow = [
    {
      content: "Total",
      colSpan: 9,
      styles: { halign: "right", fontStyle: "bold" },
    },
    {
      content: totals.emiAmount.toLocaleString("en-IN"),
      styles: { fontStyle: "bold" },
    },
    { content: "",colSpan:2, styles: { halign: "center" } },
  ];

    autoTable(doc, {
          startY: currentY + 5,
          head: [tableColumn],
          body: tableRows,
          foot: [totalRow],
          showFoot: "lastPage",
          theme: "grid",
          styles: {
            fontSize: 7,
            cellPadding: 2,
            halign: "center",
          },
          headStyles: {
            fillColor: [240, 240, 240], // Light grey header
            textColor: 0,
            fontStyle: "bold",
            lineWidth: 0.1,
          },
          footStyles: {
            fillColor: [230, 230, 230],
            textColor: 0,
            fontStyle: "bold",
          },
          columnStyles: {
            1: { halign: "left" }, // Customer Name
            4: { halign: "right" }, 
            5: { halign: "right" },
            6: { halign: "right" },
            7: { halign: "right" },
          },
          didDrawPage: (data) => {
            // Page Border on every page
            doc.setDrawColor(0);
            doc.setLineWidth(0.2);
            doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
          },
        });
    doc.save("GroupLoan_EmiDue_Report.pdf");
  };


    // 🔹 ✅ Total Calculation
  const totals = filteredData.reduce(
    (acc, c) => {
      acc.emiAmount += c.emiAmount || 0;
      acc.outstanding += c.outstanding || 0;
      return acc;
    },
    { emiAmount: 0, outstanding:0 },
  );

    /* ---------------- REACT-SELECT OPTIONS MAPPING ---------------- */
  const centerOptions = centers.map((c) => ({
    value: c.id,
    label: c.CenterName,
  }));

  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: `${g.groupname} (${g.groupcode})`,
  }));
  return (
    <div className="gledr-container">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Emi Due Report
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>

      {/* FILTER */}
      <div className="gledr-filterBox">
        <div className="gledr-filter-title">Filter By</div>

        <div className="gledr-filter-wrapper">
          <div className="gledr-filter-item">
            <div className="gledr-filter">
              <label>Loan Type</label>
              <select
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="FIXED">Fixed Loan</option>
                <option value="INDIVIDUAL">Individual Loan</option>
              </select>
            </div>

            <div className="gledr-filter">
              <label>Select Center</label>
              <Select
              options={centerOptions}
                value={selectedCenter}
                onChange={(option) => {
                  setSelectedCenter(option);
                  setSelectedGroup(null);
                }}
                placeholder="Search or Select Center"
                isClearable
                isSearchable
                styles={customSelectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="gledr-filter">
              <label>Select Group</label>
              <Select
                options={groupOptions}
                value={selectedGroup}
                onChange={(option) => setSelectedGroup(option)}
                placeholder={
                  selectedCenter ? "Search or Select Group" : "Select Center First"
                }
                isDisabled={!selectedCenter}
                isClearable
                isSearchable
                styles={customSelectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="gledr-filter">
              <label>Select Filter</label>
              <select
                value={dayFilter}
                onChange={(e) => setDayFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            <div className="gledr-filter">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="gledr-filter">
              <label>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="gledr-filter-submit-btn">
            <button className="submit-btn" onClick={handleFilter}>
              submit
            </button>
          </div>
        </div>
      </div>

      {/* EXPORT */}
      {filteredData.length > 0 && (
        <div className="gledr-pdf-download-btns">
          <button
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: loading ? "#a5b4fc" : "#4f46e5", // Loading state me color light ho jayega
              color: "#ffffff",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              transition: "all 0.2s ease-in-out",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = "#4338ca";
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = "#4f46e5";
            }}
            onClick={handlebulkpay}
          >
            <ArrowUpRight size={16} />
            {loading ? "Processing..." : "Bulk Pay"}
          </button>
          <button onClick={exportCSV}>
            <Download size={15} /> CSV
          </button>
          <button onClick={exportExcel}>
            <Download size={15} /> Excel
          </button>
          <button onClick={exportPDF}>
            <Download size={15} /> PDF
          </button>
        </div>
      )}

      {/* 🔍 SEARCH + PER PAGE */}
      <div className="gledr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div className="gledr-report-filter-inputs">
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              const value = e.target.value;
              setItemsPerPage(value === "All" ? "All" : Number(value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span> entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="gledr-report-table-container">
        <table className="gledr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Loan Account No</th>
              <th>Loan Type</th>
              <th>Loan Amount</th>
              <th>Center Name</th>
              <th>Group Name</th>
              <th>Member Name</th>
              <th>Mobile No</th>
              <th>Emi No.</th>
              <th>EMI Amount</th>
              <th>Outstanding</th>
              <th>Due Date</th>
              <th>Status</th>
              <th
                style={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignContent: "center",
                }}
              >
                Select For Bulk Pay{" "}
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="14" align="center">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length ? (
              filteredData.map((r, i) => {
                const status = r.status?.toLowerCase() || "pending";
                return (
                  <tr key={r.id}>
                    <td>
                      {itemsPerPage === "All"
                        ? i + 1
                        : (currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td>{r.loanAccount?.loanAccountNumber || "N/A"}</td>
                    {/* <td>{r.loanAccount?.loanType || "N/A"}</td> */}
                    <td>Group Loan {r.loanAccount?.loanType&&`(${r.loanAccount.loanType})`}</td>
                    <td>{r.loanAccount?.loanAmount}</td>
                    <td>{r.loanAccount?.centerName || "N/A"}</td>
                    <td>{r.loanAccount?.groupName || "N/A"}</td>
                    <td>{r.loanAccount?.memberName || "N/A"}</td>
                    <td>{r.member?.mobileNumber || "N/A"}</td>
                    <td>{`Emi No:- ${r.installmentNo}`}</td>
                    <td>₹{`${r.emiAmount ? r.emiAmount : 0}`}</td>
                    <td>₹{`${r.outstanding ? r.outstanding : 0}`}</td>
                    <td>{new Date(r.dueDate).toLocaleDateString("en-GB")}</td>
                    <td
                      className={
                        status === "paid"
                          ? "status-paid"
                          : status === "pending"
                            ? "status-pending"
                            : "status-overdue"
                      }
                      style={{ textTransform: "capitalize" }}
                    >
                      {r.status.toLowerCase()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {r.status.toLowerCase() !== "paid" && (
                        <input
                          type="checkbox"
                          checked={selectedIds.some((item) => item.id === r.id)}
                          onChange={(e) =>
                            handleSelectClick(r, e.target.checked)
                          }
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="14" align="center">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
                <tr className="CollectionReport-total-row">
                  <td
                    colSpan="9"
                    style={{ textAlign: "right", fontWeight: "bold" }}
                  >
                    Total:
                  </td>
                  <td style={{ fontWeight: "bold" }}>
                    Rs. {totals.emiAmount.toLocaleString()}
                  </td>
                  <td style={{ fontWeight: "bold" }}>
                    Rs. {totals.outstanding.toLocaleString()}
                  </td>
                  
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
        </table>
      </div>

      {/* PAGINATION */}
      {/* <div className="gledr-pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          ⬅ Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next ➡
        </button>
      </div> */}
      {itemsPerPage !== "All" && (
        <div className="gledr-pagination">
          <button
            disabled={currentPage === 1 || filteredData.length === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ⬅ Prev
          </button>

          <span>
            {filteredData.length === 0
              ? "No data"
              : `Page ${currentPage} of ${totalPages}`}
          </span>

          <button
            disabled={currentPage === totalPages || filteredData.length === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupLoanEmiDueReport;
